import { getDataOrRunCommandInBackbone } from "../APIs/GetDataFromBackbone";
import { commandError, isNetworkError, run } from '../Utils/accClient';
import { getUserName } from "../Utils/GeneralUtils";
import Cookies from 'js-cookie';
import axios from 'axios';
import {
    isMockMode,
    mockFetchTasks,
    mockMarkCompleted,
    mockAddTask,
    mockUpdateTask,
    mockDeleteTask,
    mockFetchEmployees,
    mockGetEvidenceUploadUrl,
    mockAddMemberTestimonial,
    mockExportMemberTestimonials,
    mockListMemberTestimonials,
} from "./mockTasks";
import { normalizeVerification, parseFlowKind, parseFlowOrder } from './taskVerification';
import { progressUser } from './progressExpiry';
import { resizeImageForUpload } from './imageUtils';
import {
    mockGetBoothPowerStatus,
    mockStartShiftFlow,
    mockGetShiftFlow,
    mockFinishShiftFlow,
    mockCompleteFlowStep,
    mockGetActiveNudges,
    mockRespondNudge,
    mockPhaseOverride,
} from "../ShiftFlow/mockShiftFlow";

/**
 * Tasks are backed by the ACC Lambda (`TaskBoard`) + DynamoDB. Commands return
 * the shared `{ success, data }` envelope; `run` (Utils/accClient) parses it
 * and throws typed errors (`code`, `details`, `isNetwork`) so callers can use
 * try/catch. Gym employees still come from Backbone (gym profiles), so that
 * one call is unchanged.
 */
export { isNetworkError };

let trainerNameCache = null;

/** Test helper — reset cached gym FullName resolution. */
export const clearTrainerNameCache = () => {
    trainerNameCache = null;
};

/**
 * Map the Google login display name to the gym roster FullName so task
 * assignee lookups and admin metrics match DynamoDB / EmployeeDetails.
 */
export const resolveTrainerName = async () => {
    if (trainerNameCache) return trainerNameCache;

    const cookieName = getUserName();
    if (!cookieName) return null;

    if (isMockMode()) {
        trainerNameCache = cookieName;
        return trainerNameCache;
    }

    try {
        const gymProfile = await getDataOrRunCommandInBackbone({
            action: 'getGymProfile',
            gymId: Number(getPreferredGymId()) || Number(DEFAULT_GYM_ID) || 2,
        });
        const employees = Object.values(gymProfile?.EmployeeDetails || {});
        const userData = Cookies.get('user');
        const email = userData ? (JSON.parse(userData).email || '') : '';
        const emailLocal = email.split('@')[0]?.toLowerCase() || '';

        for (const employee of employees) {
            const fullName = employee.FullName;
            if (!fullName) continue;
            if (fullName === cookieName || fullName.toLowerCase() === cookieName.toLowerCase()) {
                trainerNameCache = fullName;
                return trainerNameCache;
            }
            const username = (employee.Username || '').toLowerCase();
            if (username && (email.toLowerCase() === username || emailLocal === username)) {
                trainerNameCache = fullName;
                return trainerNameCache;
            }
        }
    } catch (error) {
        console.error('Error resolving trainer name:', error);
    }

    trainerNameCache = cookieName;
    return trainerNameCache;
};

export const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };

    if (dateString.length === 10) {
        dateString = `${dateString}T23:59:59`;
    }
    return new Date(dateString).toLocaleDateString(undefined, options);
};

/** Parse YYYY-MM-DD as a local calendar day (avoid UTC midnight weekday shift). */
export const parseLocalDate = (value) => {
    if (!value) return null;
    const text = String(value).trim();
    const dayOnly = text.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(dayOnly) && (text.length === 10 || text[10] === 'T' || text[10] === ' ')) {
        const [year, month, day] = dayOnly.split('-').map(Number);
        return new Date(year, month - 1, day);
    }
    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) return null;
    parsed.setHours(0, 0, 0, 0);
    return parsed;
};

export const sortTasks = (tasks, sortBy, sortOrder) => {
    return [...tasks].sort((a, b) => {
        if (a[sortBy] < b[sortBy]) return sortOrder === 'asc' ? -1 : 1;
        if (a[sortBy] > b[sortBy]) return sortOrder === 'asc' ? 1 : -1;
        return 0;
    });
};

export const formatRecurrenceLabel = (recurrence) => {
    if (recurrence === 'thrice_weekly') return '3× weekly';
    return recurrence;
};

export const SHIFT_PHASE_LABELS = {
    open: 'Open',
    all_day: 'All day',
    close: 'Close',
    mid: 'All day',
};

export const SHIFT_GROUP_LABELS = {
    open: 'Open',
    all_day: 'All day',
    close: 'Close',
};

/** Meridian defaults (Mon=0 … Sun=6). Matches published BBVR hours. */
export const MERIDIAN_BY_WEEKDAY = {
    0: { open: '06:00', close: '20:00' },
    1: { open: '06:00', close: '20:00' },
    2: { open: '06:00', close: '20:00' },
    3: { open: '06:00', close: '20:00' },
    4: { open: '06:00', close: '20:00' },
    5: { open: '09:00', close: '15:00' },
    6: { open: '11:00', close: '15:00' },
};

/** Defaults when GymProfiles.OperatingHours is unset (gym-local UTC−6). */
export const DEFAULT_OPERATING_HOURS = {
    open: '06:00',
    close: '20:00',
    timezoneOffsetHours: -6,
    openWindowMinutes: 120,
    closeWindowMinutes: 90,
    byWeekday: { ...MERIDIAN_BY_WEEKDAY },
};

/** Default Meridian gym id used for hours + roster name resolve. */
export const DEFAULT_GYM_ID = '2';

const PREFERRED_GYM_KEY = 'bbvr_preferred_gym';

/** Sticky gym preference (localStorage); falls back to DEFAULT_GYM_ID. */
export const getPreferredGymId = () => {
    try {
        const stored = localStorage.getItem(PREFERRED_GYM_KEY);
        if (stored != null && String(stored).trim() !== '' && String(stored) !== 'All') {
            return String(stored).trim();
        }
    } catch (e) {
        /* ignore storage failures */
    }
    return DEFAULT_GYM_ID;
};

export const setPreferredGymId = (id) => {
    try {
        if (id == null || String(id).trim() === '' || String(id) === 'All') return;
        localStorage.setItem(PREFERRED_GYM_KEY, String(id).trim());
    } catch (e) {
        /* ignore storage failures */
    }
};

/** Gym business calendar day (UTC−6), matching TaskBoard.today(). */
export const businessToday = (now = new Date(), offsetHours = DEFAULT_OPERATING_HOURS.timezoneOffsetHours) => {
    const offset = Number.isFinite(Number(offsetHours)) ? Number(offsetHours) : -6;
    const shifted = new Date(now.getTime() + offset * 3600 * 1000);
    const year = shifted.getUTCFullYear();
    const month = String(shifted.getUTCMonth() + 1).padStart(2, '0');
    const day = String(shifted.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/** Local Date at midnight for the current business day. */
export const businessTodayDate = (now = new Date(), offsetHours = DEFAULT_OPERATING_HOURS.timezoneOffsetHours) => {
    const [year, month, day] = businessToday(now, offsetHours).split('-').map(Number);
    return new Date(year, month - 1, day);
};

const parseHhMm = (value) => {
    const text = String(value || '').trim();
    const match = /^(\d{1,2}):(\d{2})/.exec(text);
    if (!match) return null;
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour > 23 || minute > 59) return null;
    return hour * 60 + minute;
};

/** Convert JS Sunday=0 day to Mon=0…Sun=6. */
export const toMondayIndex = (jsSundayIndex) => (jsSundayIndex === 0 ? 6 : jsSundayIndex - 1);

export const hoursForWeekday = (hours, weekdayMon0) => {
    const cfg = { ...DEFAULT_OPERATING_HOURS, ...(hours || {}) };
    const by = cfg.byWeekday || {};
    const key = String(weekdayMon0);
    if (Object.prototype.hasOwnProperty.call(by, key) || Object.prototype.hasOwnProperty.call(by, weekdayMon0)) {
        const day = by[key] ?? by[weekdayMon0];
        if (day == null) return null;
        if (parseHhMm(day.open) != null && parseHhMm(day.close) != null) {
            return { open: day.open, close: day.close };
        }
    }
    return { open: cfg.open, close: cfg.close };
};

/** Derive open | all_day | close from gym hours + current time. */
export const getCurrentShiftPhase = (hours = DEFAULT_OPERATING_HOURS, now = new Date()) => {
    const cfg = { ...DEFAULT_OPERATING_HOURS, ...(hours || {}) };
    const offset = Number(cfg.timezoneOffsetHours);
    const local = new Date(now.getTime() + (Number.isFinite(offset) ? offset : -6) * 3600 * 1000);
    // Use UTC getters after offset apply so browser TZ doesn't double-shift.
    const localMins = local.getUTCHours() * 60 + local.getUTCMinutes();
    const weekday = toMondayIndex(local.getUTCDay());
    const dayHours = hoursForWeekday(cfg, weekday);
    if (!dayHours) return 'all_day';
    const openMins = parseHhMm(dayHours.open) ?? parseHhMm(DEFAULT_OPERATING_HOURS.open);
    const closeMins = parseHhMm(dayHours.close) ?? parseHhMm(DEFAULT_OPERATING_HOURS.close);
    const openEnd = openMins + Number(cfg.openWindowMinutes || 120);
    let closeStart = closeMins - Number(cfg.closeWindowMinutes || 90);
    if (closeStart < openEnd) closeStart = openEnd;
    if (localMins < openEnd) return 'open';
    if (localMins >= closeStart) return 'close';
    return 'all_day';
};

/**
 * Gym-local 'HH:MM' when the close window opens today (close minus
 * closeWindowMinutes, never earlier than the end of the open window so it
 * agrees with getCurrentShiftPhase). Null on a closed day.
 */
export const closeWindowStart = (hours = DEFAULT_OPERATING_HOURS, now = new Date()) => {
    const cfg = { ...DEFAULT_OPERATING_HOURS, ...(hours || {}) };
    const offset = Number(cfg.timezoneOffsetHours);
    const local = new Date(now.getTime() + (Number.isFinite(offset) ? offset : -6) * 3600 * 1000);
    const dayHours = hoursForWeekday(cfg, toMondayIndex(local.getUTCDay()));
    if (!dayHours) return null;
    const openMins = parseHhMm(dayHours.open) ?? parseHhMm(DEFAULT_OPERATING_HOURS.open);
    const closeMins = parseHhMm(dayHours.close) ?? parseHhMm(DEFAULT_OPERATING_HOURS.close);
    const openEnd = openMins + Number(cfg.openWindowMinutes || 120);
    const start = Math.max(closeMins - Number(cfg.closeWindowMinutes || 90), openEnd);
    const hh = String(Math.floor(start / 60) % 24).padStart(2, '0');
    const mm = String(start % 60).padStart(2, '0');
    return `${hh}:${mm}`;
};

/** Gym-local 'HH:MM' when today's open window starts (the open time). Null on a closed day. */
export const openWindowStart = (hours = DEFAULT_OPERATING_HOURS, now = new Date()) => {
    const cfg = { ...DEFAULT_OPERATING_HOURS, ...(hours || {}) };
    const offset = Number(cfg.timezoneOffsetHours);
    const local = new Date(now.getTime() + (Number.isFinite(offset) ? offset : -6) * 3600 * 1000);
    const dayHours = hoursForWeekday(cfg, toMondayIndex(local.getUTCDay()));
    if (!dayHours) return null;
    const openMins = parseHhMm(dayHours.open) ?? parseHhMm(DEFAULT_OPERATING_HOURS.open);
    return `${String(Math.floor(openMins / 60) % 24).padStart(2, '0')}:${String(openMins % 60).padStart(2, '0')}`;
};

/** Gym-local 'HH:MM' the doors close today. Null on a closed day. */
export const todayClose = (hours = DEFAULT_OPERATING_HOURS, now = new Date()) => {
    const cfg = { ...DEFAULT_OPERATING_HOURS, ...(hours || {}) };
    const offset = Number(cfg.timezoneOffsetHours);
    const local = new Date(now.getTime() + (Number.isFinite(offset) ? offset : -6) * 3600 * 1000);
    const dayHours = hoursForWeekday(cfg, toMondayIndex(local.getUTCDay()));
    if (!dayHours) return null;
    const closeMins = parseHhMm(dayHours.close) ?? parseHhMm(DEFAULT_OPERATING_HOURS.close);
    return `${String(Math.floor(closeMins / 60) % 24).padStart(2, '0')}:${String(closeMins % 60).padStart(2, '0')}`;
};

/** Whether the gym-local clock has not yet reached today's closing time. */
export const beforeClosingTime = (hours = DEFAULT_OPERATING_HOURS, now = new Date()) => {
    const close = parseHhMm(todayClose(hours, now));
    if (close == null) return false;
    const cfg = { ...DEFAULT_OPERATING_HOURS, ...(hours || {}) };
    const offset = Number(cfg.timezoneOffsetHours);
    const local = new Date(now.getTime() + (Number.isFinite(offset) ? offset : -6) * 3600 * 1000);
    return local.getUTCHours() * 60 + local.getUTCMinutes() < close;
};

/** A lock-up task the server wants done only once the last member has left. */
export const isAfterLastMemberTask = (task) => task?.verification?.afterLastMember === true;

/**
 * Whether checking this task off should ask "Members already gone?" first.
 * Only lock-up tasks, and only while the gym is still open — after closing time
 * the answer is not in doubt, so the question would be noise. Either answer
 * completes the task; `meta.membersGone` is what the nightly audit reads.
 */
export const needsMembersGoneAsk = (task, hours, now = new Date()) =>
    isAfterLastMemberTask(task) && beforeClosingTime(hours, now);

/**
 * Gym-local 'HH:MM' when the phase's window opens today; null on a closed day
 * or for phases without a window. The window is advisory — open and close work
 * can be done at any hour — so this only feeds informational copy.
 */
export const flowUnlocksAt = (phase, hours = DEFAULT_OPERATING_HOURS, now = new Date()) => {
    if (phase === 'open') return openWindowStart(hours, now);
    if (phase === 'close') return closeWindowStart(hours, now);
    return null;
};

/**
 * '18:30' -> '6:30pm'. Also accepts an ISO stamp carrying a gym-local offset
 * ('2026-09-05T18:30:00-06:00'), reading its wall-clock part. Empty string for
 * anything unparseable.
 */
export const formatClock = (hhmm) => {
    const text = String(hhmm || '').trim();
    const iso = /T(\d{2}:\d{2})/.exec(text);
    const mins = parseHhMm(iso ? iso[1] : text);
    if (mins == null) return '';
    const hour24 = Math.floor(mins / 60);
    const minute = String(mins % 60).padStart(2, '0');
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    return `${hour12}:${minute}${hour24 < 12 ? 'am' : 'pm'}`;
};

/** Advisory chip copy for a phase window, e.g. 'Close window 6:30pm'. Empty when there is none today. */
export const flowWindowLabel = (phase, hours = DEFAULT_OPERATING_HOURS, now = new Date()) => {
    const clock = formatClock(flowUnlocksAt(phase, hours, now));
    if (!clock) return '';
    return `${phase === 'open' ? 'Open' : 'Close'} window ${clock}`;
};

/** Collapse map: expand current open/close window; all-day stays open always. */
export const defaultCollapsedPhases = (currentPhase) => ({
    open: currentPhase !== 'open',
    all_day: false,
    close: currentPhase !== 'close',
    other: true,
});

/** Whether a phase section should render collapsed (all-day is never collapsed). */
export const isPhaseSectionCollapsed = (collapsedPhases, phaseKey, currentPhase) => {
    if (phaseKey === 'all_day') {
        return false;
    }
    if (collapsedPhases && Object.prototype.hasOwnProperty.call(collapsedPhases, phaseKey)) {
        return Boolean(collapsedPhases[phaseKey]);
    }
    return phaseKey !== currentPhase;
};

export const fetchOperatingHours = async (gymId = getPreferredGymId()) => {
    const resolvedId = String(gymId || getPreferredGymId());
    if (isMockMode()) {
        const hours = { ...DEFAULT_OPERATING_HOURS, gymId: resolvedId, configured: false };
        return { ...hours, currentShiftPhase: mockPhaseOverride() || getCurrentShiftPhase(hours) };
    }
    try {
        const data = await run('get_operating_hours', [resolvedId]);
        if (!data || typeof data !== 'object') {
            const hours = { ...DEFAULT_OPERATING_HOURS, gymId: resolvedId };
            return { ...hours, currentShiftPhase: getCurrentShiftPhase(hours) };
        }
        return {
            ...DEFAULT_OPERATING_HOURS,
            ...data,
            currentShiftPhase: data.currentShiftPhase || getCurrentShiftPhase(data),
        };
    } catch (e) {
        const hours = { ...DEFAULT_OPERATING_HOURS, gymId: resolvedId };
        return { ...hours, currentShiftPhase: getCurrentShiftPhase(hours) };
    }
};

/** Weekly tasks are always anytime-on-shift (all day). */
export const effectiveShiftPhase = (task) => {
    if (task?.recurrence === 'weekly') return 'all_day';
    const phase = task?.shiftPhase;
    if (phase === 'mid') return 'all_day';
    return phase || '';
};

/** HT Center keeps open / all day / close as separate buckets. */
export const shiftPhaseGroup = (phase) => {
    if (phase === 'open') return 'open';
    if (phase === 'all_day' || phase === 'mid') return 'all_day';
    if (phase === 'close') return 'close';
    return 'other';
};

/** A step of the closing walk: its own flowKind wins over its shift phase. */
export const isCloseTask = (task) =>
    (task?.flowKind || shiftPhaseGroup(effectiveShiftPhase(task))) === 'close';

/** The closing walk, opened on one step. `step` is read by ShiftFlow on load. */
export const closeWalkStepPath = (taskId) =>
    `/shift-flow/close?step=${encodeURIComponent(taskId || '')}`;

/**
 * The task ids the loaded close flow actually walks. Empty for a flow that
 * failed to read, a closed day (`E_FLOW_CLOSED_DAY`), or a day with no steps —
 * which is exactly when a handoff would dead-end.
 */
export const closeWalkStepIds = (flow) =>
    new Set((flow?.steps || []).map((step) => step?.taskId).filter(Boolean));

/**
 * Whether checking this task off should open the closing walk instead of
 * completing inline. Once the close window is open the walk is the close path:
 * closers were clearing all seven close tasks from the flat list in seconds and
 * no guided close was ever started, so the list hands off to the walk rather
 * than competing with it. Outside the window, and for open / all-day work,
 * nothing changes. `canCompleteInline` is the manager override.
 *
 * `walkStepIds` is the close flow's own step ids: a row with nowhere to land in
 * the walk (unreadable flow, closed day, no steps today, or a close task that
 * is not a step) completes inline as before rather than sending Done to a page
 * that cannot check it off. Testimonial tasks keep their inline capture button
 * for the same reason.
 */
export const opensCloseWalk = (task, currentPhase, canCompleteInline = false, walkStepIds = null) => {
    if (currentPhase !== 'close' || canCompleteInline) return false;
    if (!isCloseTask(task) || task?.verification?.method === 'testimonial') return false;
    const ids = walkStepIds instanceof Set ? walkStepIds : new Set(walkStepIds || []);
    return ids.has(task?.taskId);
};

/**
 * `meta` for a close task checked off outside the guided walk (HT Center, the
 * daily report) while the trainer has a close run open. Without the run id the
 * nightly audit reads honest work as done outside the walk. Null when there is
 * no live run, or the task is not part of closing.
 */
export const closeRunMeta = (run, task) =>
    run && run.status === 'in_progress' && !run.expired && isCloseTask(task)
        ? { flowRunId: run.id }
        : null;

export const formatShiftPhase = (phase) => SHIFT_PHASE_LABELS[phase] || '';

export const formatTaskShiftPhase = (task) => formatShiftPhase(effectiveShiftPhase(task));

export const isOptionalTask = (task) => task?.requirementLevel === 'optional';

export const isPoolTask = (task) => task?.assignedTo === 'On Shift Staff';

/** Fallback SOP links for seed tasks if DynamoDB hasn't been updated yet. */
const SEED_SOP_ARTICLE_IDS = {
    'seed-open-disarm-unlock': 'sop-gym-opening',
    'seed-open-lights-tvs': 'sop-gym-opening',
    'seed-open-boot-booths': 'sop-gym-opening',
    'seed-open-garments': 'sop-gym-opening',
    'seed-open-protein': 'sop-facility-cleaning-and-maintenance',
    'seed-open-bathrooms': 'sop-cleaning-bathrooms',
    'seed-close-shutdown-booths': 'sop-booth-shutdown',
    'seed-close-furniture': 'sop-cleaning-furniture',
    'seed-close-mop': 'sop-mopping',
    'seed-close-vacuum': 'sop-vacuuming',
    'seed-close-bathrooms': 'sop-cleaning-bathrooms',
    'seed-close-ipad-14': 'sop-gym-closing',
    'seed-close-arm-lock': 'sop-gym-closing',
    'seed-mon-windows': 'sop-cleaning-windows',
    'seed-mon-protein-behind': 'sop-facility-cleaning-and-maintenance',
    'seed-tue-deepbooth-1': 'sop-deep-cleaning-a-booth',
    'seed-wed-fountain': 'sop-cleaning-drinking-fountain',
    'seed-thu-deepbooth-2': 'sop-deep-cleaning-a-booth',
    'seed-fri-panels-mop': 'sop-cleaning-booth-outside',
    'seed-sat-deepbath': 'sop-deep-cleaning-a-bathroom',
    'seed-sun-furniture': 'sop-cleaning-furniture',
    'seed-monthly-deepbath': 'sop-deep-cleaning-a-bathroom',
    'seed-monthly-furniture': 'sop-cleaning-furniture',
    'seed-monthly-vacuum-maint': 'sop-vacuum-maintenance',
};

export const getTaskKnowledgeArticleId = (task) => {
    if (!task) return '';
    if (task.knowledgeArticleId) return task.knowledgeArticleId;
    const id = task.taskId || task.id || '';
    return SEED_SOP_ARTICLE_IDS[id] || '';
};

export const taskSopPath = (articleId) =>
    articleId ? `/knowledge-base?article=${encodeURIComponent(articleId)}` : '';

export const getTaskCompletionLabel = (task) => {
    if (task?.completionOutcome === 'not_needed') return 'Reopen';
    if (task?.recurrence !== 'thrice_weekly' || task.status === 'Completed') {
        return task?.status === 'Completed' ? 'Reopen' : 'Done';
    }
    const count = task.periodCompletionCount || 0;
    const target = task.recurrenceTarget || 3;
    return `Done (${count}/${target})`;
};

/**
 * Shift tasks are for today — past incomplete work is missed, not overdue, and
 * does not carry into the active list. Recurring tasks surface on their
 * scheduled day (weekly = startDate weekday), not whenever dueDate was rolled.
 */
export const isTaskRelevantForToday = (task) => {
    const today = businessTodayDate();

    const taskDueDate = parseLocalDate(task.dueDate);
    if (!taskDueDate) return false;

    const taskStartDate = parseLocalDate(task.startDate);

    if (!task.recurrence) {
        return taskDueDate.getTime() === today.getTime();
    }

    if (taskStartDate && today < taskStartDate) {
        return false;
    }

    switch (task.recurrence) {
        case 'daily':
            return true;
        case 'weekly':
            // Anchor weekday comes from startDate; dueDate may be rolled to "today"
            // by the API on any day of the week, so do not trust dueDate alone.
            if (!taskStartDate) return false;
            return today.getDay() === taskStartDate.getDay();
        case 'thrice_weekly':
            return true;
        case 'monthly':
            return Boolean(taskStartDate && today.getDate() === taskStartDate.getDate());
        case 'yearly':
            return Boolean(
                taskStartDate
                && today.getDate() === taskStartDate.getDate()
                && today.getMonth() === taskStartDate.getMonth()
            );
        default:
            return taskDueDate.getTime() === today.getTime();
    }
};

export const filterTasks = (tasks, filter, showCompletedTasks, includeUpcoming = false) => {
    return tasks.filter(task => {
        if (!showCompletedTasks && task.status === 'Completed') return false;
        if (filter.status && task.status !== filter.status) return false;
        if (filter.priority && task.priority !== filter.priority) return false;

        // When upcoming is included, show everything that passes the status/
        // priority filters (future-dated tasks included). Otherwise keep the
        // default "what needs doing now" relevance gate.
        if (includeUpcoming) return true;
        return isTaskRelevantForToday(task);
    });
};

export const fetchTasks = async (viewMode, username) => {
    if (isMockMode()) {
        return mockFetchTasks(viewMode, username);
    }
    if (viewMode === 'assigned') {
        // A trainer's own tasks plus the shared On-Shift pool.
        const [userTasks, onShiftTasks] = await Promise.all([
            run('get_assignee_tasks', [username]),
            run('get_assignee_tasks', ['On Shift Staff']),
        ]);
        return [...(userTasks || []), ...(onShiftTasks || [])];
    }
    return (await run('get_all_tasks', [])) || [];
};

// ---------------------------------------------------------------------------
// Guided shift flow (ShiftFlow.py) + booth heartbeat status (BoothStatus.py).
// The first argument is a flow kind: 'open' | 'close' | 'cleaning' |
// 'sop:<articleId>'. The mock computes phase/lock from the default hours so the
// flow, the HT Center and the daily report agree offline; the server owns it
// for real.
// ---------------------------------------------------------------------------

const mockFlowContext = (kind) => {
    const hours = DEFAULT_OPERATING_HOURS;
    return {
        currentPhase: mockPhaseOverride() || getCurrentShiftPhase(hours),
        unlocksAt: flowUnlocksAt(kind, hours),
        businessDate: businessToday(),
    };
};

export const getBoothPowerStatus = async (gymId = getPreferredGymId()) => {
    const resolvedId = String(gymId || getPreferredGymId());
    if (isMockMode()) return mockGetBoothPowerStatus(resolvedId);
    return run('get_booth_power_status', [resolvedId]);
};

export const startShiftFlow = async (kind, gymId = getPreferredGymId()) => {
    const resolvedId = String(gymId || getPreferredGymId());
    if (isMockMode()) return mockStartShiftFlow(kind, resolvedId, mockFlowContext(kind));
    return run('start_shift_flow', [kind, resolvedId]);
};

export const getShiftFlow = async (kind, gymId = getPreferredGymId()) => {
    const resolvedId = String(gymId || getPreferredGymId());
    if (isMockMode()) return mockGetShiftFlow(kind, resolvedId, mockFlowContext(kind));
    return run('get_shift_flow', [kind, resolvedId]);
};

/**
 * Finish the walk. A transport failure queues it instead of losing it: the gym
 * Wi-Fi drops at closing time, and a finish that never lands leaves the report
 * gate shut with nothing for the trainer to press. `boothSnapshotAt` is when
 * the client last saw the booth reading it is finishing on, so a replay hours
 * later can be weighed against the readings from that moment rather than from
 * whenever the network came back. Resolves to the server data, or undefined
 * when the queue took it.
 */
export const finishShiftFlow = async (kind, gymId = getPreferredGymId(), options = {}) => {
    const resolvedId = String(gymId || getPreferredGymId());
    const payload = {};
    const override = String(options?.boothOverrideReason || '').trim();
    if (override) payload.boothOverrideReason = override;
    const snapshotAt = String(options?.boothSnapshotAt || '').trim();
    if (snapshotAt) payload.boothSnapshotAt = snapshotAt;
    if (isMockMode()) return mockFinishShiftFlow(kind, resolvedId, payload, mockFlowContext(kind));
    let data;
    try {
        data = await run('finish_shift_flow', [kind, resolvedId, payload]);
    } catch (error) {
        if (!error.isNetwork) throw error;
        enqueueFlowCall('finish', 'finish_shift_flow', [kind, resolvedId, payload]);
        console.warn('Queued flow finish for retry:', kind, error.message);
        return undefined;
    }
    // Check-offs first: a queued step lives in the completion queue, and a
    // finish replayed ahead of it is refused for steps still open.
    await flushCompletionQueue();
    await flushFlowQueue();
    return data;
};

// ---------------------------------------------------------------------------
// Close nudges (ShiftFlow.py). The server watches the booths after the shutdown
// step lands and asks that closer to look again; the client shows what is open
// and reports back. Only the caller's own open nudges for today come back.
// ---------------------------------------------------------------------------

export const getActiveNudges = async (gymId = getPreferredGymId()) => {
    const resolvedId = String(gymId || getPreferredGymId());
    const data = isMockMode()
        ? await mockGetActiveNudges(resolvedId, businessToday())
        : await run('get_active_nudges', [resolvedId]);
    return Array.isArray(data?.nudges) ? data.nudges : [];
};

/**
 * `response` is 'rechecked' or 'not_needed'; a recheck re-reads the booths
 * server-side, so a `stillOn` naming booths means the nudge is still open and
 * the banner stays. Resolves to `{ nudge, stillOn }`.
 */
export const respondNudge = async (id, response, note = '') => {
    const answer = response === 'not_needed' ? 'not_needed' : 'rechecked';
    const trimmed = String(note || '').trim();
    if (isMockMode()) return mockRespondNudge(String(id), answer, trimmed);
    return run('respond_nudge', trimmed ? [String(id), answer, trimmed] : [String(id), answer]);
};

// ---------------------------------------------------------------------------
// Member testimonials (MemberTestimonials.py). The server validates, counts the
// trainer's own entries for the business day, posts every one to the managers
// Slack channel, and checks off the trainer's `testimonial` task once the
// target is reached — the client only reports what came back.
// ---------------------------------------------------------------------------

/**
 * `entry` is `{ memberName, kind, content, notes, consent }`: kind
 * `'quote' | 'video'`, content the quote text or an http(s) link, `consent`
 * whether the member is happy to be quoted publicly. Resolves to
 * `{ testimonial, todayCount, target, taskCompleted, slackPosted }`. A rejected
 * entry throws with `code: 'E_TESTIMONIAL_INVALID'` and `details.field` naming
 * the field to flag, so the form can mark it instead of showing a banner.
 */
export const addMemberTestimonial = async (gymId = getPreferredGymId(), entry = {}) => {
    const resolvedId = String(gymId || getPreferredGymId());
    const payload = {
        memberName: String(entry.memberName || '').trim(),
        kind: entry.kind === 'video' ? 'video' : 'quote',
        content: String(entry.content || '').trim(),
        notes: String(entry.notes || '').trim(),
        consent: Boolean(entry.consent),
    };
    if (isMockMode()) return mockAddMemberTestimonial(resolvedId, payload, businessToday());
    return run('add_member_testimonial', [resolvedId, payload]);
};

/** Today's testimonials for a gym, or another business day's when `date` is set. */
export const listMemberTestimonials = async (gymId = getPreferredGymId(), date = '') => {
    const resolvedId = String(gymId || getPreferredGymId());
    const businessDate = String(date || '').trim();
    if (isMockMode()) return mockListMemberTestimonials(resolvedId, businessDate || businessToday());
    return run('list_member_testimonials', businessDate ? [resolvedId, businessDate] : [resolvedId]);
};

/**
 * Manager export of a date range, inclusive, as `{ csv, count, consentedCount }`.
 * Only the consented rows are quotable in public, so the count of them comes
 * back separately rather than being something the reader has to tally.
 */
export const exportMemberTestimonials = async (gymId = getPreferredGymId(), startDate = '', endDate = '') => {
    const resolvedId = String(gymId || getPreferredGymId());
    const start = String(startDate || '').trim();
    const end = String(endDate || '').trim();
    if (isMockMode()) return mockExportMemberTestimonials(resolvedId, start, end);
    return run('export_member_testimonials', [resolvedId, start, end]);
};

/**
 * Per-step state for a `sop:<articleId>` walkthrough; task-backed kinds record
 * their steps through `complete_task` instead. `undo: true` clears one step, a
 * `skipReason` skips it, and `photoKey` attaches evidence to it, with
 * `photoTakenAt` when the original file carried an EXIF capture time. Resolves
 * to the updated flow.
 *
 * `clientTs` is the device clock at the tap, which is what the server measures
 * a photo's age against — the same provenance a task completion sends in meta.
 *
 * Resolves to the updated flow, or undefined when a transport failure put the
 * step on the queue; the walk then reads as saved-offline rather than losing
 * the tap.
 */
export const completeFlowStep = async (kind, stepId, options = {}, gymId = getPreferredGymId()) => {
    const resolvedId = String(gymId || getPreferredGymId());
    const payload = { clientTs: new Date().toISOString() };
    const photoKey = String(options?.photoKey || '').trim();
    const photoTakenAt = String(options?.photoTakenAt || '').trim();
    const skipReason = String(options?.skipReason || '').trim();
    if (photoKey) {
        payload.photoKey = photoKey;
        if (photoTakenAt) payload.photoTakenAt = photoTakenAt;
    }
    if (skipReason) payload.skipReason = skipReason;
    if (options?.undo) payload.undo = true;
    if (isMockMode()) return mockCompleteFlowStep(kind, resolvedId, stepId, payload, mockFlowContext(kind));
    let data;
    try {
        data = await run('complete_flow_step', [kind, resolvedId, stepId, payload]);
    } catch (error) {
        if (!error.isNetwork) throw error;
        enqueueFlowCall('step', 'complete_flow_step', [kind, resolvedId, stepId, payload]);
        console.warn('Queued flow step for retry:', stepId, error.message);
        return undefined;
    }
    await flushCompletionQueue();
    await flushFlowQueue();
    return data;
};

// ---------------------------------------------------------------------------
// Photo evidence (TaskEvidence.py). The server picks bucket and key; the client
// only downsizes, PUTs to the presigned URL and passes photoKey with Done.
// ---------------------------------------------------------------------------

export const EVIDENCE_CONTENT_TYPE = 'image/jpeg';

export const getEvidenceUploadUrl = async (taskId, gymId = getPreferredGymId()) => {
    const resolvedId = String(gymId || getPreferredGymId());
    if (isMockMode()) return mockGetEvidenceUploadUrl(taskId, resolvedId);
    return run('get_task_evidence_upload_url', [taskId, resolvedId]);
};

const uploadStageError = (message, cause) => {
    // Only a server rejection (from `run`) carries a real errorCode; an axios
    // failure's `code` is transport noise like ERR_NETWORK.
    const fromServer = cause?.isNetwork === false;
    const error = commandError(message, {
        code: fromServer ? cause.code : null,
        details: fromServer ? cause.details : null,
        isNetwork: isNetworkError(cause),
        cause,
    });
    error.stage = 'upload';
    return error;
};

/**
 * PUT the JPEG to the presigned URL. Throws an error with `stage: 'upload'`;
 * nothing is queued because the URL expires in five minutes and the photo
 * only exists in the browser, so the trainer retries from the preview.
 */
export const uploadEvidence = async (uploadUrl, blob) => {
    if (isMockMode()) return;
    try {
        await axios.put(uploadUrl, blob, { headers: { 'Content-Type': EVIDENCE_CONTENT_TYPE } });
    } catch (error) {
        throw uploadStageError('Photo did not upload. Try again.', error);
    }
};

/**
 * Resize, fetch a presigned URL for `scopeId` (a task id, or an article id for a
 * walkthrough step) and PUT the bytes; resolves to the `photoKey` to send with
 * the completion. Every failure carries `stage: 'upload'`, which is what tells
 * the callers never to enqueue it.
 */
export const uploadEvidencePhoto = async ({ scopeId, file, gymId = undefined }) => {
    try {
        const blob = await resizeImageForUpload(file);
        const target = await getEvidenceUploadUrl(scopeId, gymId);
        if (!target?.uploadUrl || !target?.photoKey) {
            throw new Error('Upload link was incomplete.');
        }
        await uploadEvidence(target.uploadUrl, blob);
        return target.photoKey;
    } catch (error) {
        throw error?.stage === 'upload'
            ? error
            : uploadStageError(error?.message || 'Photo did not upload. Try again.', error);
    }
};

/**
 * Upload, then complete the task with `meta.photoKey`. Failures before the
 * completion never enqueue; once the object is in S3 the completion follows the
 * normal path (network errors queue and replay with the same photoKey).
 * `onStage('upload' | 'complete')` lets the UI say "Uploading photo" then
 * "Checking photo".
 */
export const captureAndCompleteWithPhoto = async ({ id, taskId, file, reason = '', meta = null, onStage = null }) => {
    if (onStage) onStage('upload');
    const photoKey = await uploadEvidencePhoto({ scopeId: taskId, file, gymId: meta?.gymId });
    if (onStage) onStage('complete');
    return markTaskAsCompleted(id, taskId, 'done', reason, { ...(meta || {}), photoKey });
};

/**
 * Fold PhotoCapture's second argument into a step's completion meta. Only the
 * capture time crosses over, so the capture control cannot widen what a
 * completion claims; a photo with no EXIF leaves the meta exactly as it was.
 */
export const withPhotoMeta = (meta, photoMeta) => (
    photoMeta?.photoTakenAt
        ? { ...(meta || {}), photoTakenAt: photoMeta.photoTakenAt }
        : (meta || null)
);

/**
 * Completion meta rides along as the 4th positional arg so the shift audit can
 * tell a live check-off from a replayed one. Defaults are the client clock and
 * the sticky gym; caller keys (flowRunId, photoKey, membersGone) override only
 * when set. Empty strings and nullish values are dropped rather than sent as
 * blanks; `false` is not one of them, so `membersGone: false` — a real answer,
 * and the one the audit cares about — crosses intact.
 */
const buildCompletionMeta = (meta) => {
    const out = { clientTs: new Date().toISOString(), gymId: getPreferredGymId() };
    Object.entries(meta || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') out[key] = value;
    });
    return out;
};

/**
 * Resolves to the server's completion data (the task plus
 * `completionVerification` and `evidence` for photo tasks), or undefined when
 * the request was queued for replay.
 */
export const markTaskAsCompleted = async (id, taskId, outcome = 'done', reason = '', meta = null) => {
    const entry = {
        taskId,
        outcome: outcome || 'done',
        reason: reason || '',
        meta: buildCompletionMeta(meta),
    };
    if (isMockMode()) {
        return mockMarkCompleted(id, entry.taskId, entry.outcome, entry.reason, entry.meta);
    }
    let data;
    try {
        data = await run('complete_task', [entry.taskId, entry.outcome, entry.reason, entry.meta]);
    } catch (error) {
        // Only a transport failure is worth replaying (gym Wi-Fi flakes at close).
        // A server rejection such as E_TASK_SOP_INCOMPLETE must reach the trainer.
        if (!error.isNetwork) throw error;
        enqueueCompletion(entry);
        console.warn('Queued task completion for retry:', taskId, error.message);
        return undefined;
    }
    await flushCompletionQueue();
    // A check-off is usually the first call to get through after the Wi-Fi
    // comes back, so it is also what carries a stranded finish home.
    await flushFlowQueue();
    return data;
};

const COMPLETION_QUEUE_KEY = 'bbvr_task_complete_queue';

const readCompletionQueue = () => {
    try {
        const raw = JSON.parse(localStorage.getItem(COMPLETION_QUEUE_KEY) || '[]');
        return Array.isArray(raw) ? raw : [];
    } catch (e) {
        return [];
    }
};

const writeCompletionQueue = (items) => {
    try {
        localStorage.setItem(COMPLETION_QUEUE_KEY, JSON.stringify(items));
    } catch (e) {
        /* ignore */
    }
};

/**
 * One entry per task per trainer, holding that trainer's last intent for it:
 * outcome, `undo`, and the `clientTs` of the tap that said so. A replay applies
 * final state (the same outcome again is idempotent, `undo` un-completes), so
 * the newest tap is the only one worth sending — and its own `clientTs` is what
 * the server's repeat suppression compares, so an older one cannot be kept.
 * Trainers share a tablet, so the entry records who queued it.
 */
const enqueueCompletion = (entry) => {
    const queue = readCompletionQueue();
    const queued = { ...entry, user: progressUser(), queuedAt: Date.now() };
    const at = queue.findIndex((item) => item.taskId === queued.taskId && (item.user || '') === queued.user);
    if (at === -1) queue.push(queued);
    else queue[at] = queued;
    writeCompletionQueue(queue.slice(-40));
};

/**
 * A replay whose tap happened on an earlier business day is work the server
 * refuses (`E_TASK_REPLAY_STALE`) — today's board is a different day's board —
 * so it is dropped here rather than sent to be rejected.
 */
const isStaleReplay = (item, today = businessToday()) => {
    const raw = item?.meta?.clientTs;
    if (!raw) return false;
    const ms = new Date(raw).getTime();
    if (Number.isNaN(ms)) return false;
    return businessToday(new Date(ms)) !== today;
};

// An entry with no `user` was queued by the frontend before entries recorded
// one; leaving it unowned would strand it in the queue for good.
const isOwnedBy = (item, user) => !item.user || item.user === user;

/**
 * Replay offline Done / Not needed / Blocked actions once ACC is reachable.
 * `replayed: true` tells the audit the server timestamp is not when the work
 * happened; `meta.clientTs` is. Entries the server definitively rejects are
 * dropped: retrying a task whose SOP steps are unmet, or whose photo was
 * refused, would fail every time and block the queue behind it.
 *
 * Only the signed-in trainer's own entries replay. The tablet is shared, and
 * the server credits the caller, so a check-off queued by whoever was on the
 * earlier shift waits for them to sign back in. Entries whose tap fell on an
 * earlier business day are dropped unsent: the server refuses them.
 */
export const flushCompletionQueue = async () => {
    if (isMockMode()) return { flushed: 0, rejected: 0, remaining: 0 };
    const queue = readCompletionQueue();
    if (!queue.length) return { flushed: 0, rejected: 0, remaining: 0 };
    const user = progressUser();
    const today = businessToday();
    const mine = [];
    const remaining = [];
    let flushed = 0;
    let rejected = 0;
    queue.forEach((item) => {
        if (!isOwnedBy(item, user)) remaining.push(item);
        else if (isStaleReplay(item, today)) rejected += 1;
        else mine.push(item);
    });
    if (!mine.length) {
        writeCompletionQueue(remaining);
        return { flushed, rejected, remaining: remaining.length };
    }
    for (const item of mine) {
        try {
            await run('complete_task', [item.taskId, item.outcome, item.reason, { ...item.meta, replayed: true }]);
            flushed += 1;
        } catch (error) {
            if (error.isNetwork) {
                remaining.push(item);
            } else {
                rejected += 1;
                console.warn('Dropped queued task completion:', item.taskId, error.message);
            }
        }
    }
    writeCompletionQueue(remaining);
    return { flushed, rejected, remaining: remaining.length };
};

/** How many of this trainer's check-offs are saved offline and still waiting to send. */
export const queuedCompletionCount = () => {
    const user = progressUser();
    return readCompletionQueue().filter((item) => isOwnedBy(item, user)).length;
};

// ---------------------------------------------------------------------------
// Guided-walk queue. Check-offs already survive a Wi-Fi drop; finishing the
// walk and writing a walkthrough step did not, which is what left a trainer at
// the report gate with no way through. Same treatment, one store, `kind` says
// which command an entry replays.
// ---------------------------------------------------------------------------

const FLOW_QUEUE_KEY = 'bbvr_flow_queue';

const readFlowQueue = () => {
    try {
        const raw = JSON.parse(localStorage.getItem(FLOW_QUEUE_KEY) || '[]');
        return Array.isArray(raw) ? raw : [];
    } catch (e) {
        return [];
    }
};

const writeFlowQueue = (items) => {
    try {
        localStorage.setItem(FLOW_QUEUE_KEY, JSON.stringify(items));
    } catch (e) {
        /* ignore */
    }
};

/**
 * Unlike a check-off, these are a sequence rather than a final state: a step
 * written twice is not the same as a step written once, and a finish only makes
 * sense after the steps before it. So entries are appended and never collapsed.
 */
const enqueueFlowCall = (kind, command, args) => {
    writeFlowQueue([
        ...readFlowQueue(),
        { kind, command, args, user: progressUser(), queuedAt: Date.now() },
    ].slice(-40));
};

/** Whether this trainer has a finish for that kind still waiting on the network. */
export const hasQueuedFlowFinish = (kind = 'close') => {
    const user = progressUser();
    return readFlowQueue().some(
        (item) => item.kind === 'finish' && item.args?.[0] === kind && isOwnedBy(item, user)
    );
};

/**
 * A queued call from an earlier business day replays onto a run that no longer
 * exists, so it is dropped unsent the way a stale check-off is. `queuedAt` is
 * the device clock at the tap, which is the same provenance `isStaleReplay`
 * reads out of `meta.clientTs`.
 */
const isStaleFlowCall = (item, today = businessToday()) => {
    const ms = Number(item?.queuedAt);
    if (!Number.isFinite(ms) || ms <= 0) return false;
    return businessToday(new Date(ms)) !== today;
};

// A run the server says is not ready yet — steps still open, or a run that has
// not been started on its side. Unlike an unknown step these come good once the
// entry ahead of them lands, so they go back on the queue instead of being
// dropped. `attempts` caps that: three refusals and it is a real rejection.
const HELD_FLOW_CODES = new Set(['E_FLOW_STEPS_OPEN', 'E_FLOW_NOT_STARTED']);
const FLOW_RETRY_CAP = 3;

// The last server rejection a replay hit, for whichever screen shows it next.
// The flushes happen inside the data layer (a check-off, a step, a task read),
// so the caller that renders the notice is never the one holding the result.
let flowQueueNotice = '';

/** Dropped on sign-out: a rejection of one trainer's close is not the next trainer's news. */
export const clearFlowQueueNotice = () => {
    flowQueueNotice = '';
};

/** Read and clear the pending rejection notice; empty when there is nothing to say. */
export const takeFlowQueueNotice = () => {
    const notice = flowQueueNotice;
    flowQueueNotice = '';
    return notice;
};

/**
 * Replay queued finishes and steps oldest first. Order is the point, so the
 * first entry the network still refuses stops the flush and keeps everything
 * behind it queued. A definitive server rejection drops that one entry instead:
 * a finish a teammate already sent, or a step on a run that has moved on, would
 * fail the same way forever and hold every later entry hostage.
 *
 * Only the signed-in trainer's own entries replay — the tablet is shared and
 * the server credits the caller.
 *
 * Resolves to `{ replayed, rejected, held }`: how many landed, how many the
 * server refused for good, and how many are still on the queue.
 */
export const flushFlowQueue = async () => {
    const empty = { replayed: 0, rejected: 0, held: 0 };
    if (isMockMode()) return empty;
    const queue = readFlowQueue();
    if (!queue.length) return empty;
    const user = progressUser();
    const today = businessToday();
    const mine = [];
    const remaining = [];
    let replayed = 0;
    let rejected = 0;
    queue.forEach((item) => {
        if (!isOwnedBy(item, user)) remaining.push(item);
        else if (isStaleFlowCall(item, today)) rejected += 1;
        else mine.push(item);
    });
    for (let i = 0; i < mine.length; i += 1) {
        const item = mine[i];
        try {
            await run(item.command, item.args);
            replayed += 1;
        } catch (error) {
            if (error.isNetwork) {
                remaining.push(...mine.slice(i));
                break;
            }
            const attempts = (item.attempts || 0) + 1;
            if (HELD_FLOW_CODES.has(error.code) && attempts < FLOW_RETRY_CAP) {
                remaining.push({ ...item, attempts });
                continue;
            }
            rejected += 1;
            flowQueueNotice = error.message || '';
            console.warn('Dropped queued flow call:', item.command, error.message);
        }
    }
    writeFlowQueue(remaining);
    return { replayed, rejected, held: remaining.length };
};

export const addNewTask = async (username, newTask) => {
    if (isMockMode()) {
        await mockAddTask(username, newTask);
        return;
    }
    const payload = {
        taskName: newTask.taskName,
        description: newTask.description,
        startDate: new Date().toISOString(),
        dueDate: newTask.dueDate,
        assignedTo: newTask.assignedTo,
        assignedBy: newTask.assignedBy,
        status: 'New',
        priority: newTask.priority,
        recurrence: newTask.recurrence === 'none' ? null : newTask.recurrence,
        shiftPhase: (newTask.recurrence === 'weekly' ? 'all_day' : (newTask.shiftPhase || '')),
        requirementLevel: newTask.requirementLevel || 'required',
        knowledgeArticleId: newTask.knowledgeArticleId || '',
        verification: normalizeVerification(newTask.verification),
        flowOrder: parseFlowOrder(newTask.flowOrder),
        flowKind: parseFlowKind(newTask.flowKind),
    };
    if (newTask.memberImpact != null) payload.memberImpact = Boolean(newTask.memberImpact);
    await run('create_task', [payload]);
};

/** Typed fields are normalized here so every caller sends the same shape. */
export const updateExistingTask = async (id, taskId, updates) => {
    const payload = { ...(updates || {}) };
    if ('verification' in payload) payload.verification = normalizeVerification(payload.verification);
    if ('flowOrder' in payload) payload.flowOrder = parseFlowOrder(payload.flowOrder);
    if ('flowKind' in payload) payload.flowKind = parseFlowKind(payload.flowKind);
    if (isMockMode()) {
        await mockUpdateTask(id, taskId, payload);
        return;
    }
    await run('update_task', [taskId, payload]);
};

export const deleteTask = async (id, taskId) => {
    if (isMockMode()) {
        await mockDeleteTask(id, taskId);
        return;
    }
    await run('delete_task', [taskId]);
};

export const shouldRefreshTasks = (lastFetchTime) => {
    const currentTime = new Date().getTime();
    const oneHour = 60 * 60 * 1000;
    return !lastFetchTime || (currentTime - lastFetchTime > oneHour);
};

export const setupTaskRefresh = (refreshCallback) => {
    const intervalId = setInterval(() => {
        refreshCallback(true);
    }, 60 * 60 * 1000);

    return () => clearInterval(intervalId);
};

export const fetchEmployees = async (gymId = 2) => {
    if (isMockMode()) {
        return mockFetchEmployees();
    }
    try {
        const gymProfile = await getDataOrRunCommandInBackbone({
            action: 'getGymProfile',
            gymId: gymId
        });
        if (gymProfile && gymProfile.EmployeeDetails) {
            const employees = Object.values(gymProfile.EmployeeDetails).map(employee => ({
                label: employee.FullName,
                value: employee.FullName
            }));

            return [...employees, {label: "On Shift Staff", value: "On Shift Staff"}];
        }
        return [];
    } catch (error) {
        console.error("Error fetching employees:", error);
        return [];
    }
};